<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleModal from '../EditTitleModal.vue';
import EditDescriptionModal from '../EditDescriptionModal.vue';
import EditButtonModal from '../EditButtonModal.vue';
import ImageUploadModal from '../ImageUploadModal.vue';

const b = bem('cta-image-module');

export default {
  components: {
    PencilIcon,
    EditTitleModal,
    EditDescriptionModal,
    EditButtonModal,
    ImageUploadModal,
  },
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
    blockIndex: {
      type: Number,
      default: 0,
    },
    companyColors: {
      type: Object,
      default: () => ({
        color1: '',
        color2: '',
        color3: '',
      }),
    },
  },
  emits: ['update'],
  data() {
    return {
      isTitleModalVisible: false,
      isDescriptionModalVisible: false,
      isButtonModalVisible: false,
      isImageModalVisible: false,
    };
  },
  computed: {
    content() {
      if (this.moduleData?.parsedContent) {
        return this.moduleData.parsedContent;
      }
      if (!this.moduleData?.content) return {};
      if (typeof this.moduleData.content === 'string') {
        try {
          return JSON.parse(this.moduleData.content);
        } catch {
          return {};
        }
      }
      return this.moduleData.content;
    },
    imagePosition() {
      const templateId = this.moduleData?.templateId
        || this.moduleData?.parsedContent?.templateId
        || 'cta-image-left';
      if (templateId === 'cta-image-right') {
        return 'right';
      }
      return 'left';
    },
    displayTitle() {
      if (this.isMobile && this.content.titleMobile) {
        return this.content.titleMobile;
      }
      return this.content.title || 'Title';
    },
    displayDescription() {
      if (this.isMobile && this.content.descriptionMobile) {
        return this.content.descriptionMobile;
      }
      return this.content.description || 'Description';
    },
    displayButtonText() {
      if (this.isMobile && this.content.buttonTextMobile) {
        return this.content.buttonTextMobile;
      }
      return this.content.buttonText || 'Button Text';
    },
    displayImage() {
      if (this.isMobile && this.content.imageMobile) {
        return this.content.imageMobile;
      }
      return this.content.image || '';
    },
    isEvenBlock() {
      return this.blockIndex % 2 === 1;
    },
    buttonBackgroundColor() {
      if (!this.isEditMode && this.companyColors.color2) {
        return this.companyColors.color2;
      }
      return '#242424';
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    openTitleModal() {
      if (this.isEditMode) {
        this.isTitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (this.isEditMode) {
        this.isDescriptionModalVisible = true;
      }
    },
    openButtonModal() {
      if (this.isEditMode) {
        this.isButtonModalVisible = true;
      }
    },
    openImageModal() {
      if (this.isEditMode) {
        this.isImageModalVisible = true;
      }
    },
    handleTitleSave(data) {
      this.updateContent({
        ...this.content,
        title: data.title,
        titleMobile: data.titleMobile,
      });
      this.isTitleModalVisible = false;
    },
    handleDescriptionSave(data) {
      this.updateContent({
        ...this.content,
        description: data.description,
        descriptionMobile: data.descriptionMobile,
      });
      this.isDescriptionModalVisible = false;
    },
    handleButtonSave(data) {
      this.updateContent({
        ...this.content,
        buttonText: data.buttonText,
        buttonTextMobile: data.buttonTextMobile,
        buttonLink: data.buttonLink,
        backgroundButtonColor: this.companyColors.color2 || '#242424',
      });
      this.isButtonModalVisible = false;
    },
    handleImageSave(data) {
      this.updateContent({
        ...this.content,
        image: data.image,
        imageMobile: data.imageMobile,
      });
      this.isImageModalVisible = false;
    },
    updateContent(newContent) {
      const contentWithBackground = {
        ...newContent,
        backgroundColor: this.isEvenBlock ? (this.companyColors.color3 || 'transparent') : 'transparent',
      };
      this.$emit('update', {
        ...this.moduleData,
        content: JSON.stringify(contentWithBackground),
      });
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, 'image-right': imagePosition === 'right', popup: isPopup })">
    <div
      v-if="!isMobile"
      :class="b('image', { editable: isEditMode, popup: isPopup })"
      :style="displayImage ? {
        backgroundImage: `url(${displayImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}"
      role="button"
      tabindex="0"
      @click="openImageModal"
      @keydown.enter="openImageModal"
    >
      <span v-if="!displayImage" :class="b('placeholder-text')">
        <span :class="b('placeholder-plus')">+</span><br>CLICK TO UPLOAD
      </span>
    </div>

    <div :class="b('content', {
      left: contentPosition === 'left',
      mobile: isMobile,
      popup: isPopup
    })">
      <div :class="b('top-part', { mobile: isMobile })">
        <div
          :class="b('title-row', { editable: isEditMode })"
          role="button"
          tabindex="0"
          @click="openTitleModal"
          @keydown.enter="openTitleModal"
        >
          <span :class="b('title', { mobile: isMobile })">{{ displayTitle }}</span>
          <PencilIcon v-if="isEditMode" :size="20" color="#7f7f7f" :class="b('edit-icon')" />
        </div>
        <div
          v-if="isMobile"
          :class="b('image', { editable: isEditMode, popup: isPopup, mobile: true })"
          :style="displayImage ? {
            backgroundImage: `url(${displayImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}"
        />
        <div
          :class="b('desc-row', { editable: isEditMode })"
          role="button"
          tabindex="0"
          @click="openDescriptionModal"
          @keydown.enter="openDescriptionModal"
        >
          <span :class="b('desc', { mobile: isMobile })">{{ displayDescription }}</span>
          <PencilIcon v-if="isEditMode" :size="16" color="#7f7f7f" :class="b('edit-icon')" />
        </div>
      </div>
      <div
        :class="b('button', { editable: isEditMode, mobile: isMobile, popup: isPopup })"
        :style="{ backgroundColor: buttonBackgroundColor }"
        role="button"
        tabindex="0"
        @click="openButtonModal"
        @keydown.enter="openButtonModal"
      >
        <span :class="b('button-text')">{{ displayButtonText }}</span>
        <PencilIcon v-if="isEditMode" :size="16" color="#fff" :class="b('edit-icon')" />
      </div>
    </div>

    <EditTitleModal
      :visible="isTitleModalVisible"
      :title="content.title"
      :title-mobile="content.titleMobile"
      @close="isTitleModalVisible = false"
      @save="handleTitleSave"
    />

    <EditDescriptionModal
      :visible="isDescriptionModalVisible"
      :description="content.description"
      :description-mobile="content.descriptionMobile"
      @close="isDescriptionModalVisible = false"
      @save="handleDescriptionSave"
    />

    <EditButtonModal
      :visible="isButtonModalVisible"
      :button-text="content.buttonText"
      :button-text-mobile="content.buttonTextMobile"
      :button-link="content.buttonLink"
      @close="isButtonModalVisible = false"
      @save="handleButtonSave"
    />

    <ImageUploadModal
      :visible="isImageModalVisible"
      :image="content.image"
      :image-mobile="content.imageMobile"
      @close="isImageModalVisible = false"
      @save="handleImageSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.cta-image-module {
  display: flex;
  align-items: flex-start;
  gap: 64px;
  width: 1540px;
  padding: 64px 0;

  @include mediaMinMax(1025, $normal) {
    width: vw-full(1540);
    gap: vw-full(64);
    padding: vw-full(64) 0;
  }
  &_popup {
    width: 1200px;
    gap: 50px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1200);
      gap: vw-full(50);
    }
  }
  &_mobile {
    padding: 40px 0;
  }
  &_image-right {
    flex-direction: row-reverse;
  }

  &__image {
    flex-shrink: 0;
    width: 867px;
    height: 608px;
    border-radius: 24px;
    background: #d9d9d9;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(867);
      height: vw-full(608);
      border-radius: vw-full(24);
    }

    &_editable:hover {
      background: #c5c5c5;
    }
    &_popup {
      width: 675px;
      height: 474px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(675);
        height: vw-full(474);
      }
    }
    &_mobile {
      width: 380px;
      height: 269px;
      margin: 0 auto;
    }
  }

  &__placeholder-text {
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 400;
    color: #7f7f7f;
    text-align: center;
    line-height: 1.4;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
  }

  &__placeholder-plus {
    font-size: 56px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(56);
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    height: 608px;
    padding: 158px 0 75px;
    gap: 26px;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(608);
      padding: vw-full(158) 0 vw-full(75);
    }
    &_popup {
      height: 474px;
      padding: 123px 0 59px;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(474);
        padding: vw-full(123) 0 vw-full(59);
      }
    }
    &_mobile {
      height: fit-content;
      padding: 0 16px;
      justify-content: flex-start;
      gap: 20px;
    }
  }

  &__top-part {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 32px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(32);
    }
    &_mobile {
      gap: 20px;
    }
  }

  &__title-row,
  &__desc-row {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 16px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
    }

    &_editable {
      cursor: pointer;
      padding: 5px 10px;
      margin: -5px -10px;
      border-radius: 5px;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.05);
      }
    }
  }

  &__title {
    font-family: Montserrat, sans-serif;
    font-size: 40px;
    font-weight: 600;
    color: #293241;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }
    &_popup {
      font-size: 28px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(28);
      }
    }
    &_mobile {
      width: 100%;
      text-align: center;
    }
  }

  &__desc {
    font-family: Montserrat, sans-serif;
    font-size: 19px;
    font-weight: 400;
    color: #5e5d5d;
    text-align: left;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(19);
    }
    &_mobile {
      width: 100%;
      text-align: center;
    }
  }

  &__button {
    width: 480px;
    height: 64px;
    border-radius: 20px;
    background: #242424;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    cursor: pointer;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(480);
      height: vw-full(64);
      border-radius: vw-full(20);
      gap: vw-full(16)
    }

    &_popup {
      width: 375px;
      height: 50px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(375);
        height: vw-full(50);
      }
    }
    &_mobile {
      width: 332px;
      height: 56px;
    }
  }

  &__button-text {
    font-family: Montserrat, sans-serif;
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    text-align: center;
    text-transform: uppercase;
    transition: background 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(22);
    }

    &:hover {
      background: #3a3a3a;
    }
  }

  &__edit-icon {
    flex-shrink: 0;
    margin-top: 5px;
  }
}
</style>
